import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "@store/hooks";
import {
  addOrgMember,
  createOrgInviteToken,
  getOrganizationBySlug,
  getOrganizationMembers,
  listOrgInviteTokens,
  removeOrgMember,
  revokeOrgInviteToken,
  updateOrgMember,
} from "@services/api";
import {
  Organization,
  OrganizationInviteToken,
  OrganizationMembershipWithUser,
} from "@/types/models";
import { useNotification } from "@shared/contexts/NotificationContext";

// Build a shareable URL for an invite token. The token is the only
// thing the recipient needs; we don't include org slug since the
// redemption endpoint resolves it from the token.
const inviteUrl = (token: string) =>
  `${window.location.origin}/orgs/join/${token}`;

const isTokenActive = (t: OrganizationInviteToken) => {
  if (t.revoked_at) return false;
  if (t.expires_at && new Date(t.expires_at) < new Date()) return false;
  return true;
};

const formatExpiry = (t: OrganizationInviteToken) => {
  if (t.revoked_at) return "Revoked";
  if (!t.expires_at) return "No expiration";
  const exp = new Date(t.expires_at);
  if (exp < new Date()) return "Expired";
  return `Expires ${exp.toLocaleDateString()}`;
};

const EXPIRY_CHOICES: { label: string; days: number }[] = [
  { label: "1 day", days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "No expiration", days: 0 },
];

const OrgSettingsScreen = () => {
  const { slug } = useParams<{ slug: string }>();
  const currentUserId = useAppSelector((state) => state.user.userId);
  const { showSuccess, showError } = useNotification();

  const [org, setOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMembershipWithUser[]>([]);
  const [tokens, setTokens] = useState<OrganizationInviteToken[]>([]);
  const [loadError, setLoadError] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberAsAdmin, setNewMemberAsAdmin] = useState(false);
  const [newTokenExpiryDays, setNewTokenExpiryDays] = useState<number>(7);

  const callerMembership = useMemo(
    () => members.find((m) => m.user_id === currentUserId),
    [members, currentUserId]
  );
  const isOrgAdmin = callerMembership?.role === "org_admin";

  const fetchAll = useCallback(async () => {
    if (!slug) return;
    setLoadError("");
    const [orgResult, membersResult] = await Promise.all([
      getOrganizationBySlug(slug),
      getOrganizationMembers(slug),
    ]);
    if (!orgResult.success) {
      setLoadError(orgResult.error.message);
      return;
    }
    if (!membersResult.success) {
      setLoadError(membersResult.error.message);
      return;
    }
    setOrg(orgResult.data);
    setMembers(membersResult.data);
  }, [slug]);

  // Tokens are only visible to org_admins, so fetch them separately
  // and gracefully ignore 403s (non-admins still see the page).
  const fetchTokens = useCallback(async () => {
    if (!slug) return;
    const result = await listOrgInviteTokens(slug);
    if (result.success) {
      setTokens(result.data);
    }
  }, [slug]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Defer the token fetch until we know the caller is an org_admin
  // (avoids a guaranteed 403 in the network panel for regular members).
  useEffect(() => {
    if (isOrgAdmin) fetchTokens();
  }, [isOrgAdmin, fetchTokens]);

  const handleToggleRole = async (m: OrganizationMembershipWithUser) => {
    if (!slug) return;
    const nextRole = m.role === "org_admin" ? "member" : "org_admin";
    setBusy(true);
    const result = await updateOrgMember(slug, m.user_id, { role: nextRole });
    setBusy(false);
    if (!result.success) {
      showError(result.error.message);
      return;
    }
    showSuccess(`${m.username} is now ${nextRole.replace("_", " ")}`);
    fetchAll();
  };

  const handleToggleTier = async (m: OrganizationMembershipWithUser) => {
    if (!slug) return;
    const granted = m.tier_source !== "inherited";
    const nextTier = granted ? "annotator" : "evaluator";
    setBusy(true);
    const result = await updateOrgMember(slug, m.user_id, { tier: nextTier });
    setBusy(false);
    if (!result.success) {
      showError(result.error.message);
      return;
    }
    showSuccess(
      granted
        ? `Cleared evaluator grant for ${m.username}`
        : `Granted evaluator to ${m.username}`
    );
    fetchAll();
  };

  const handleRemove = async (m: OrganizationMembershipWithUser) => {
    if (!slug) return;
    if (
      !window.confirm(
        `Remove ${m.username} from ${org?.name ?? "this organization"}?`
      )
    ) {
      return;
    }
    setBusy(true);
    const result = await removeOrgMember(slug, m.user_id);
    setBusy(false);
    if (!result.success) {
      showError(result.error.message);
      return;
    }
    showSuccess(`Removed ${m.username}`);
    fetchAll();
  };

  const handleAdd = async () => {
    if (!slug || !newMemberEmail.trim()) return;
    setBusy(true);
    const result = await addOrgMember(
      slug,
      { email: newMemberEmail.trim() },
      newMemberAsAdmin ? "org_admin" : "member"
    );
    setBusy(false);
    if (!result.success) {
      showError(result.error.message);
      return;
    }
    showSuccess("Member added");
    setNewMemberEmail("");
    setNewMemberAsAdmin(false);
    fetchAll();
  };

  const handleCreateInvite = async () => {
    if (!slug) return;
    setBusy(true);
    const result = await createOrgInviteToken(slug, newTokenExpiryDays);
    setBusy(false);
    if (!result.success) {
      showError(result.error.message);
      return;
    }
    showSuccess("Invite link created");
    fetchTokens();
  };

  const handleCopyInvite = async (token: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      showSuccess("Invite link copied");
    } catch {
      showError("Couldn't copy to clipboard");
    }
  };

  const handleRevokeInvite = async (token: string) => {
    if (!slug) return;
    if (!window.confirm("Revoke this invite link? Anyone with the URL will no longer be able to join.")) {
      return;
    }
    setBusy(true);
    const result = await revokeOrgInviteToken(slug, token);
    setBusy(false);
    if (!result.success) {
      showError(result.error.message);
      return;
    }
    showSuccess("Invite link revoked");
    fetchTokens();
  };

  if (loadError) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography color="error">{loadError}</Typography>
      </Container>
    );
  }
  if (!org) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading…</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h3" gutterBottom>
        {org.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Slug: <code>{org.slug}</code>
        {org.email_domains.length > 0 && (
          <> · Domains: {org.email_domains.join(", ")}</>
        )}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {isOrgAdmin && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Invite links
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate a shareable link. Anyone who opens it and signs in
            joins this organization as a member.
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="invite-expiry-label">Expires after</InputLabel>
              <Select
                labelId="invite-expiry-label"
                label="Expires after"
                value={newTokenExpiryDays}
                onChange={(e) => setNewTokenExpiryDays(Number(e.target.value))}
              >
                {EXPIRY_CHOICES.map((c) => (
                  <MenuItem key={c.days} value={c.days}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disabled={busy}
              onClick={handleCreateInvite}
            >
              Generate invite link
            </Button>
          </Stack>

          {tokens.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Link</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens.map((t) => {
                    const active = isTokenActive(t);
                    return (
                      <TableRow key={t.token}>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                          {inviteUrl(t.token)}
                        </TableCell>
                        <TableCell>{formatExpiry(t)}</TableCell>
                        <TableCell align="right">
                          {active && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyInvite(t.token)}
                                aria-label="Copy invite link"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() => handleRevokeInvite(t.token)}
                                aria-label="Revoke invite link"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      <Typography variant="h5" gutterBottom>
        Members
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Evaluator grant (this org)</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((m) => {
              const isSelf = m.user_id === currentUserId;
              const grantActive = m.tier_source !== "inherited";
              return (
                <TableRow key={m.org_membership_id}>
                  <TableCell>
                    {m.username}
                    {isSelf && (
                      <Chip
                        size="small"
                        label="you"
                        sx={{ ml: 1 }}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isOrgAdmin && !isSelf ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => handleToggleRole(m)}
                      >
                        {m.role === "org_admin" ? "Org admin" : "Member"}
                      </Button>
                    ) : (
                      m.role === "org_admin" ? "Org admin" : "Member"
                    )}
                  </TableCell>
                  <TableCell>
                    {isOrgAdmin ? (
                      <Switch
                        checked={grantActive}
                        disabled={busy}
                        onChange={() => handleToggleTier(m)}
                        inputProps={{
                          "aria-label": `Toggle evaluator grant for ${m.username}`,
                        }}
                      />
                    ) : grantActive ? (
                      "Evaluator"
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {(isOrgAdmin || isSelf) && (
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => handleRemove(m)}
                        aria-label={isSelf ? "Leave organization" : `Remove ${m.username}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {isOrgAdmin && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Add member
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Add a registered user by email. For onboarding strangers,
            generate an invite link above.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              type="email"
              label="Email"
              placeholder="name@example.com"
              value={newMemberEmail}
              onChange={(e) => setNewMemberEmail(e.target.value)}
              sx={{ width: 360 }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Switch
                checked={newMemberAsAdmin}
                onChange={(e) => setNewMemberAsAdmin(e.target.checked)}
                inputProps={{ "aria-label": "Add as org admin" }}
              />
              <Typography variant="body2">As org admin</Typography>
            </Box>
            <Button
              variant="contained"
              disabled={busy || !newMemberEmail.trim()}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Stack>
        </Box>
      )}
    </Container>
  );
};

export default OrgSettingsScreen;
